import { useRef, useEffect, useState } from 'react'
import DOMPurify from 'dompurify'
import { useLanguage } from '../context/LanguageContext'

/**
 * Lightweight visual (WYSIWYG) editor for non-technical authors — bold shows
 * bold, headings show as headings, no raw Markdown symbols. Stores sanitized
 * HTML. Used for reseller-authored child Terms of Use so resellers don't have
 * to understand Markdown.
 *
 * The content is set into the editor only when `reseedKey` changes (mount +
 * explicit "start from…" reseed), never on every keystroke — so the caret
 * isn't reset while typing. Output is the editor's HTML.
 */
const ALLOWED = {
  ALLOWED_TAGS: ['h2', 'h3', 'p', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a', 'br', 'blockquote'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
}

export function sanitizeRichHtml(html) {
  return DOMPurify.sanitize(html || '', ALLOWED)
}

function Btn({ onMouseDown, title, children }) {
  return (
    <button type="button" onMouseDown={onMouseDown} title={title}
      style={{ padding: '4px 10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 13, cursor: 'pointer', lineHeight: 1.4 }}>
      {children}
    </button>
  )
}

export function RichTextEditor({ valueHtml, onChange, reseedKey = 0, placeholder = '' }) {
  const ref = useRef(null)

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = sanitizeRichHtml(valueHtml || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reseedKey])

  const emit = () => onChange?.(ref.current ? ref.current.innerHTML : '')
  const cmd = (name, val) => (e) => { e.preventDefault(); document.execCommand(name, false, val); ref.current?.focus(); emit() }
  function link(e) {
    e.preventDefault()
    const url = window.prompt('Link URL', 'https://')
    if (url) { document.execCommand('createLink', false, url); ref.current?.focus(); emit() }
  }
  function onPaste(e) {
    e.preventDefault()
    const html = e.clipboardData.getData('text/html')
    const text = e.clipboardData.getData('text/plain')
    if (html) document.execCommand('insertHTML', false, sanitizeRichHtml(html))
    else document.execCommand('insertText', false, text)
    emit()
  }

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
      <style>{`
        .rte-area:empty:before { content: attr(data-ph); color: #94a3b8; }
        .rte-area:focus { outline: none; }
        .rte-area h2 { font-size: 17px; margin: 16px 0 6px; color: #0f172a; font-weight: 700; }
        .rte-area h3 { font-size: 15px; margin: 12px 0 6px; color: #0f172a; font-weight: 700; }
        .rte-area p { margin: 0 0 10px; }
        .rte-area ul, .rte-area ol { padding-left: 22px; margin: 0 0 10px; }
        .rte-area li { margin: 0 0 4px; }
        .rte-area strong, .rte-area b { color: #0f172a; }
        .rte-area a { color: #2563eb; }
      `}</style>
      <div style={{ display: 'flex', gap: 4, padding: 6, background: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        <Btn onMouseDown={cmd('bold')} title="Bold"><b>B</b></Btn>
        <Btn onMouseDown={cmd('italic')} title="Italic"><i>I</i></Btn>
        <Btn onMouseDown={cmd('formatBlock', 'H2')} title="Heading">H</Btn>
        <Btn onMouseDown={cmd('formatBlock', 'P')} title="Normal text">¶</Btn>
        <Btn onMouseDown={cmd('insertUnorderedList')} title="Bulleted list">• List</Btn>
        <Btn onMouseDown={cmd('insertOrderedList')} title="Numbered list">1. List</Btn>
        <Btn onMouseDown={link} title="Link">Link</Btn>
      </div>
      <div
        ref={ref}
        className="rte-area"
        contentEditable
        suppressContentEditableWarning
        data-ph={placeholder}
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        style={{ minHeight: 220, maxHeight: 460, overflowY: 'auto', padding: 12, fontSize: 14, lineHeight: 1.6, color: '#0f172a' }}
      />
    </div>
  )
}

const tab = (active) => ({ padding: '6px 14px', background: active ? '#0f172a' : '#fff', color: active ? '#fff' : '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' })

/** English/Français tab pair over two visual editors. */
export function BilingualRichText({ valueEn, valueFr, onChangeEn, onChangeFr, reseedKey = 0, placeholder }) {
  const { t } = useLanguage()
  const [lang, setLang] = useState('en')
  return (
    <>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <button type="button" onClick={() => setLang('en')} style={tab(lang === 'en')}>{t('english', 'English')}</button>
        <button type="button" onClick={() => setLang('fr')} style={tab(lang === 'fr')}>{t('french', 'Français')}</button>
      </div>
      <div style={{ display: lang === 'en' ? 'block' : 'none' }}>
        <RichTextEditor valueHtml={valueEn} onChange={onChangeEn} reseedKey={`en|${reseedKey}`} placeholder={placeholder} />
      </div>
      <div style={{ display: lang === 'fr' ? 'block' : 'none' }}>
        <RichTextEditor valueHtml={valueFr} onChange={onChangeFr} reseedKey={`fr|${reseedKey}`} placeholder={placeholder} />
      </div>
    </>
  )
}
